import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import {
  Users, IndianRupee, ShoppingCart, Layers, TrendingUp,
  ArrowUpRight, RefreshCw, AlertCircle, BarChart3, ShoppingBag
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar
} from "recharts";
import { useDateRange } from "@/components/DateRangeFilter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val?.toFixed?.(0) ?? val;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: {formatINR(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [category, setCategory] = useState([]);
  const [status, setStatus] = useState({ completed: false, stage: "loading" });
  const [loading, setLoading] = useState(true);
  const { filterData } = useDateRange();

  const filteredRevenue = useMemo(() => filterData(revenue, "month"), [revenue, filterData]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/processing/status`);
        setStatus(data);
        if (data.completed) {
          loadData();
        } else {
          setTimeout(checkStatus, 5000);
        }
      } catch {
        setTimeout(checkStatus, 5000);
      }
    };

    const loadData = async () => {
      try {
        const [kRes, rRes, fRes, cRes] = await Promise.all([
          axios.get(`${API}/dashboard/overview`),
          axios.get(`${API}/revenue/expansion`),
          axios.get(`${API}/lifecycle/funnel`),
          axios.get(`${API}/category/analysis`),
        ]);
        setKpis(kRes.data);
        setRevenue(rRes.data.monthly || []);
        setFunnel(fRes.data.data || []);
        setCategory(cRes.data.distribution?.slice(0, 8) || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []);

  const triggerProcessing = async () => {
    try {
      await axios.post(`${API}/processing/trigger`);
      setStatus({ completed: false, stage: "starting" });
    } catch (e) {
      console.error(e);
    }
  };

  if (!status.completed) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] gap-6" data-testid="processing-status">
        <div className="animate-pulse-glow p-6 rounded-full bg-slate-900/50 border border-slate-800">
          <RefreshCw className="w-10 h-10 text-blue-400 animate-spin" />
        </div>
        <div className="text-center">
          <h2 className="font-space text-xl font-semibold text-slate-200">Processing Data</h2>
          <p className="text-slate-400 text-sm mt-1">
            Stage: <span className="font-mono text-blue-400">{status.stage}</span>
          </p>
          <p className="text-slate-500 text-xs mt-2">Analyzing 195K+ orders across 15 categories...</p>
        </div>
        {status.stage === "not_started" && (
          <button
            onClick={triggerProcessing}
            data-testid="trigger-processing-btn"
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-md transition-colors shadow-[0_0_20px_rgba(37,99,235,0.3)]"
          >
            Start Processing
          </button>
        )}
      </div>
    );
  }

  if (loading || !kpis) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 rounded-md skeleton-shimmer" />
        ))}
        <div className="col-span-full h-64 rounded-md skeleton-shimmer" />
      </div>
    );
  }

  const kpiCards = [
    { label: "Total Revenue", value: formatINR(kpis.total_revenue), icon: IndianRupee, color: "text-emerald-400", prefix: "INR " },
    { label: "Total Customers", value: kpis.total_customers?.toLocaleString(), icon: Users, color: "text-blue-400" },
    { label: "Avg Order Value", value: formatINR(kpis.avg_order_value), icon: ShoppingCart, color: "text-amber-400", prefix: "INR " },
    { label: "Repeat Rate (365d)", value: `${kpis.repeat_purchase_rate}%`, icon: Layers, color: "text-violet-400" },
  ];

  const maxFunnel = funnel[0]?.count || 1;

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
              Dashboard
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {kpis.date_range?.from} to {kpis.date_range?.to} &middot; Website Channel
            </p>
          </div>
          <Badge variant="outline" className="text-emerald-400 border-emerald-800 bg-emerald-950/30 text-xs" data-testid="data-status-badge">
            Data Processed
          </Badge>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((kpi, i) => (
            <Card
              key={kpi.label}
              className={`kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl hover:border-slate-700 transition-all duration-300 animate-fade-in stagger-${i + 1}`}
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">{kpi.label}</span>
                  <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                </div>
                <p className={`font-space text-2xl font-bold tracking-tight ${kpi.color}`}>
                  {kpi.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Annotation: KPI Context */}
        <AnnotationBanner title="Investor Context">
          <p>This data covers our <strong className="text-amber-300">D2C website only</strong>, which contributes ~20% of total brand revenue. The remaining ~80% flows through Amazon, Flipkart, and other marketplaces — where customer-level data is not shared with brands. Actual business scale is ~5x these numbers.</p>
          <p className="mt-1.5">The {kpis.repeat_purchase_rate}% repeat rate is a <strong className="text-amber-300">conservative floor</strong> for two structural reasons: (1) Our product range is exclusively durable baby gear (strollers, cribs, walkers, high chairs) — these are single-use products with no same-SKU repeat by design. A repeat customer buys a <em>different</em> category. (2) Many website customers create new accounts to avail first-time discount coupons, making them appear as new customers in our system. The true repeat rate is higher than what this data captures.</p>
        </AnnotationBanner>

        {/* Secondary KPIs row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Orders", value: kpis.total_orders?.toLocaleString() },
            { label: "Avg LTV", value: `INR ${formatINR(kpis.avg_ltv)}` },
            { label: "Cross-Category Rate", value: `${kpis.cross_category_rate}%` },
            { label: "Repeat Customers", value: kpis.repeat_customers_365d?.toLocaleString() || "—" },
          ].map((m) => (
            <div key={m.label} className="glass-card rounded-md p-4" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">{m.label}</p>
              <p className="font-mono text-lg text-slate-200 mt-1">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Revenue Trend */}
          <Card className="lg:col-span-8 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="revenue-trend-chart">
            <CardHeader className="pb-2">
              <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                Revenue Trend (Last 12 Months)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={filteredRevenue}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={formatINR} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total_revenue" stroke="#3b82f6" fill="url(#revGrad)" strokeWidth={2} name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Lifecycle Funnel */}
          <Card className="lg:col-span-4 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="lifecycle-funnel">
            <CardHeader className="pb-2">
              <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-violet-400" />
                Lifecycle Funnel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {funnel.map((stage, i) => (
                <div key={stage.stage} data-testid={`funnel-stage-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">{stage.stage}</span>
                    <span className="font-mono text-xs text-slate-300">{stage.count?.toLocaleString()} ({stage.pct}%)</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="funnel-bar h-full rounded-full"
                      style={{
                        width: `${(stage.count / maxFunnel) * 100}%`,
                        background: `linear-gradient(90deg, #3b82f6, ${i < 3 ? '#3b82f6' : '#8b5cf6'})`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Annotation>
          The funnel drop from "1+ Orders" to "Repeat" appears steep, but this understates actual retention. Many website customers repurchase on Amazon/Flipkart (where marketplace-subsidized pricing is lower during sales). Others create new website accounts for first-time discount coupons. Additionally, our website does not offer COD or returns — customers wanting to "try before committing" naturally shift to marketplaces for their first purchase, then return to us directly once trust is established. The {kpis.cross_category_rate}% cross-category rate among identified repeat buyers confirms strong brand loyalty.
        </Annotation>

        {/* Category Distribution */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-amber-400" />
              Top Categories by Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={category} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="category" type="category" width={180}
                  tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="customers" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Customers" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
